import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

export default function Header({ minimal = false }) {
  const { user, profile, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (profile?.artist_name || profile?.stage_name || user?.email || "U")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "U";

  return (
    <header className="lq-header" data-testid="lq-header">
      <div className="header-left">
        <Link to={user ? "/dashboard" : "/"} className="lq-brand" data-testid="brand-link">Lacquer</Link>
        {!minimal && user && (
          <nav className="main-nav" data-testid="main-nav">
            <TopNavLink to={user.is_admin ? "/admin" : "/dashboard"} active={location.pathname === "/dashboard" || location.pathname === "/admin"}>
              {user.is_admin ? "Admin" : "Library"}
            </TopNavLink>
            <TopNavLink to="/resources" active={location.pathname.startsWith("/resources")} data-testid="nav-resources">
              Resources
            </TopNavLink>
            <TopNavLink to="/settings" active={location.pathname === "/settings"}>
              Settings
            </TopNavLink>
          </nav>
        )}
      </div>

      {minimal ? null : user ? (
        <div ref={ref} style={{ position: "relative" }}>
          <button className="profile-chip" onClick={() => setOpen((v) => !v)} data-testid="profile-chip">
            <span className="profile-avatar">{initials}</span>
            <span className="chip-caret">▾</span>
          </button>
          {open && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                background: "var(--bg-elev)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                minWidth: 200,
                padding: 6,
                boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
                zIndex: 60,
              }}
              className="fade-in"
              data-testid="profile-menu"
            >
              <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border-soft)", marginBottom: 4 }}>
                <div style={{ fontSize: 13, color: "var(--text)" }}>{profile?.artist_name || "Artist"}</div>
                <div className="data-line" style={{ fontSize: 10, color: "var(--text-fade)" }}>{user.email}</div>
              </div>
              <MenuButton onClick={() => { setOpen(false); navigate(user?.is_admin ? "/admin" : "/dashboard"); }} data-testid="menu-library">
                {user?.is_admin ? "Admin Console" : "Library"}
              </MenuButton>
              {user?.is_admin && (
                <MenuButton onClick={() => { setOpen(false); navigate("/dashboard"); }} data-testid="menu-my-library">
                  My Library
                </MenuButton>
              )}
              <MenuButton onClick={() => { setOpen(false); navigate("/resources"); }} data-testid="menu-resources">
                Resources
              </MenuButton>
              <MenuButton onClick={() => { setOpen(false); navigate("/settings"); }} data-testid="menu-settings">
                Profile &amp; Settings
              </MenuButton>
              <MenuButton onClick={() => { setOpen(false); navigate("/pricing"); }} data-testid="menu-pricing">
                Pricing &amp; Plans
              </MenuButton>
              <div style={{ height: 1, background: "var(--border-soft)", margin: "4px 6px" }} />
              <MenuButton onClick={() => { setOpen(false); logout(); }} data-testid="menu-signout" danger>
                Sign Out
              </MenuButton>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/pricing" className="btn btn-ghost" data-testid="header-pricing">Pricing</Link>
          <Link to="/auth" className="btn btn-light" data-testid="header-signin">Sign In</Link>
        </div>
      )}
    </header>
  );
}

function MenuButton({ children, onClick, danger, ...rest }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "9px 12px",
        background: "transparent",
        color: danger ? "var(--red-light)" : "var(--text)",
        border: 0, borderRadius: 3, cursor: "pointer",
        fontFamily: "var(--mono)", fontSize: 11,
        letterSpacing: "0.10em", textTransform: "uppercase",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(245,240,232,0.05)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      {...rest}
    >
      {children}
    </button>
  );
}

function TopNavLink({ to, active, children, ...rest }) {
  return (
    <Link
      to={to}
      style={{
        padding: "6px 10px",
        fontFamily: "var(--mono)",
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: active ? "var(--accent)" : "var(--text-mute)",
        textDecoration: "none",
        borderBottom: `1px solid ${active ? "var(--accent)" : "transparent"}`,
        transition: "color 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "var(--text)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "var(--text-mute)"; }}
      {...rest}
    >
      {children}
    </Link>
  );
}

